import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtDate } from '../../utils/helpers';
import { Spinner, EmptyState } from '../../components/common';
import toast from 'react-hot-toast';
import { Calendar, Printer, CheckCircle } from 'lucide-react';

// ── Pickup Slot Selector (Customer) ──────────────────────────────────────────
export function PickupSchedulePage() {
  const [slots, setSlots]     = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [trackingId, setTrackingId] = useState('');
  const [booking, setBooking] = useState(false);
  const [booked, setBooked]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/pickup-slots').then(r=>r.data);
      if (r.success) setSlots(r.data||{});
    } finally { setLoading(false); }
  }, []);
  useEffect(()=>{ load(); },[load]);

  const bookSlot = async () => {
    if (!selected) { toast.error('Select a pickup slot'); return; }
    setBooking(true);
    try {
      const r = await api.post('/pickup-slots/book', { slotId:selected, trackingId:trackingId||undefined }).then(r=>r.data);
      if (r.success) { setBooked(r.data); toast.success(r.message); }
    } catch(e) { toast.error(e.response?.data?.message||'Booking failed'); }
    finally { setBooking(false); }
  };

  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  if (booked) return (
    <AppShell title="Pickup Scheduled">
      <div style={{ maxWidth:480, margin:'40px auto', textAlign:'center' }}>
        <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
        <h2 style={{ color:'var(--success)', marginBottom:8 }}>Pickup Booked!</h2>
        <div style={{ fontSize:'1.1rem', marginBottom:4 }}>
          <strong>{new Date(booked.slot_date).toDateString()}</strong>
        </div>
        <div style={{ fontSize:'1.1rem', marginBottom:20, color:'var(--amber)', fontWeight:600 }}>{booked.slot_time}</div>
        {trackingId && <div style={{ fontFamily:'monospace', background:'var(--gray-100)', padding:'8px 16px', borderRadius:8, marginBottom:20 }}>{trackingId}</div>}
        <button className="btn btn-outline" onClick={()=>{ setBooked(null); setSelected(null); }}>Book Another Slot</button>
      </div>
    </AppShell>
  );

  return (
    <AppShell title="Schedule Pickup">
      <div style={{ maxWidth:680 }}>
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title"><Calendar size={14} style={{ marginRight:6, display:'inline' }}/>Select Pickup Date & Time</span></div>
          <div className="card-body">
            {loading ? <Spinner /> : Object.keys(slots).length ? (
              <div style={{ display:'grid', gap:16 }}>
                {Object.entries(slots).map(([date, daySlots])=>(
                  <div key={date}>
                    <div style={{ fontWeight:600, marginBottom:8, fontSize:'.85rem', color:'var(--navy)' }}>
                      {DAYS[new Date(date).getDay()]} — {fmtDate(date)}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:8 }}>
                      {daySlots.map(slot=>(
                        <button key={slot.slotId}
                          onClick={()=>setSelected(slot.slotId)}
                          disabled={slot.isPast || slot.available <= 0}
                          style={{
                            padding:'10px 14px', borderRadius:8, border:'2px solid',
                            borderColor: selected===slot.slotId ? 'var(--amber)' : slot.available<=0||slot.isPast ? 'var(--gray-200)' : 'var(--gray-300)',
                            background: selected===slot.slotId ? '#FFFBEB' : slot.available<=0||slot.isPast ? 'var(--gray-100)' : '#fff',
                            cursor: slot.isPast||slot.available<=0 ? 'not-allowed' : 'pointer',
                            textAlign:'left', opacity: slot.isPast||slot.available<=0 ? 0.5 : 1,
                          }}>
                          <div style={{ fontWeight:600, fontSize:'.85rem' }}>{slot.time}</div>
                          <div style={{ fontSize:'.72rem', color: slot.available<=3?'var(--danger)':'var(--gray-400)', marginTop:2 }}>
                            {slot.isPast ? 'Cutoff passed' : slot.available<=0 ? 'Full' : `${slot.available} slots left`}
                          </div>
                          {selected===slot.slotId && <CheckCircle size={14} color="var(--amber)" style={{ position:'relative', marginTop:4 }}/>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyState icon="📅" title="No available pickup slots" message="Contact support to schedule a pickup." />}
          </div>
        </div>

        {selected && (
          <div className="card">
            <div className="card-body">
              <div className="form-group">
                <label className="form-label" htmlFor="slot-tid">Tracking ID (optional)</label>
                <input id="slot-tid" className="form-control" placeholder="ECLPK..." value={trackingId} onChange={e=>setTrackingId(e.target.value)} />
              </div>
              <button className="btn btn-amber btn-lg w-full" onClick={bookSlot} disabled={booking} aria-busy={booking}>
                {booking ? 'Booking…' : 'Confirm Pickup Slot'}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ── Label Print Button (used inside tracking / booking pages) ─────────────────
export function LabelPrintButton({ trackingId, size='sm' }) {
  const printLabel = async (format='HTML') => {
    try {
      if (format === 'ZPL') {
        const r = await api.get(`/shipments/${trackingId}/label?format=ZPL`).then(r=>r.data);
        const blob = new Blob([r], { type:'text/plain' });
        const url  = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${trackingId}.zpl`; a.click();
        URL.revokeObjectURL(url);
        toast.success('ZPL label downloaded');
      } else {
        // Open HTML label in new window (auto-prints)
        const win = window.open(`${window.location.origin}/api/v1/shipments/${trackingId}/label?format=HTML`, '_blank');
        if (!win) toast.error('Allow popups to print label');
      }
    } catch(e) { toast.error('Label generation failed'); }
  };

  return (
    <div style={{ display:'inline-flex', gap:4 }}>
      <button className={`btn btn-outline btn-${size}`} onClick={()=>printLabel('HTML')} title="Print label (browser)">
        <Printer size={13}/> Print Label
      </button>
      <button className={`btn btn-outline btn-${size}`} onClick={()=>printLabel('ZPL')} title="Download ZPL for thermal printer" style={{ fontSize:'.68rem' }}>
        ZPL
      </button>
    </div>
  );
}

// ── Notification Test (Admin) ─────────────────────────────────────────────────
export function NotificationTestPage() {
  const [form, setForm] = useState({ channel:'EMAIL', recipient:'', message:'ECL Test Notification' });
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const send = async () => {
    if (!form.recipient) { toast.error('Recipient required'); return; }
    setSending(true);
    try {
      const r = await api.post('/notifications/test', form).then(r=>r.data);
      if (r.success) { setResult(r.data); toast.success('Test notification sent'); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSending(false); }
  };

  return (
    <AppShell title="Notification Gateway Test">
      <div style={{ maxWidth:520 }}>
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">Send Test Notification</span></div>
          <div className="card-body">
            <div className="alert alert-info" style={{ marginBottom:12, fontSize:'.82rem' }}>
              Configure <code>SMTP_PASS</code> (SendGrid) or <code>TWILIO_SID</code> in your <code>.env</code> to enable real delivery.
              Without these, notifications are logged as SENT in the database only.
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Channel</label>
                <select className="form-control" value={form.channel} onChange={set('channel')}>
                  <option value="EMAIL">Email</option>
                  <option value="SMS">SMS</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Recipient <span className="req">*</span></label>
                <input className="form-control" placeholder={form.channel==='EMAIL'?'test@example.com':'+92300...'}
                  value={form.recipient} onChange={set('recipient')} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea className="form-control" rows={2} value={form.message} onChange={set('message')} />
            </div>
            <button className="btn btn-primary w-full" onClick={send} disabled={sending} aria-busy={sending}>
              {sending ? 'Sending…' : 'Send Test'}
            </button>
          </div>
        </div>

        {result && (
          <div className="card">
            <div className="card-header"><span className="card-title">Result</span></div>
            <div className="card-body">
              <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:8 }}>
                <span className={`badge ${result.status==='DELIVERED'?'badge-success':result.status==='SENT'?'badge-warning':'badge-danger'}`}>{result.status}</span>
                <span style={{ fontSize:'.8rem', color:'var(--gray-500)' }}>{result.channel} → {result.recipient_contact}</span>
              </div>
              {result.status==='SENT' && (
                <div className="alert alert-warning" style={{ fontSize:'.8rem' }}>
                  Status SENT = logged to database but not dispatched. Add <code>SMTP_PASS</code> or <code>TWILIO_SID</code> to your .env to enable real sending.
                </div>
              )}
              {result.status==='DELIVERED' && (
                <div className="alert alert-success" style={{ fontSize:'.8rem' }}>
                  ✅ Notification dispatched successfully via gateway.
                </div>
              )}
              {result.error_message && (
                <div className="alert alert-danger" style={{ fontSize:'.8rem' }}>{result.error_message}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
